import React, { useCallback } from 'react';
import _ from 'lodash';
import { Field, FieldRenderProps, RenderableProps } from 'react-final-form';

import { ProductGroupFilter, ProductGroupFragment } from '@occc/graphql';
import { useGetProductGroupsLazy } from '@occc-app/dataAccessLayer/productGroup/queries';
import { useChainId } from '@occc-app/Chain/hooks';

import { AutocompleteProps, FormInputProps } from './types';
import { LazyAutocomplete } from './LazyAutocomplete';
import { ILazyAutocompleteProps } from './LazyAutocomplete/types';

import { showErrorOnBlurOrDirty } from '../inputs/FinalForm/utils';

interface IProductGroupSelectContainerProps extends Pick<AutocompleteProps, 'onFocus' | 'onBlur'> {
  value: ProductGroupFragment[];
  onChange: (values: ProductGroupFragment[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  errorText?: string;
  inputProps?: FormInputProps;
  makeFilter?: MakeFilter;
}

export type MakeFilter = (
  chainId: string,
  selectedProductGroup?: string,
  selectedProductGroupIds?: string[],
) => ProductGroupFilter;

export const defaultMakeFilter: MakeFilter = (
  chainId,
  selectedProductGroup,
  selectedProductGroupIds,
) => {
  const filter: ProductGroupFilter = {
    isDeleted: false,
  };
  const setFilterValue = (propName: string, propValue: any) =>
    propValue ? _.set(filter, propName, propValue) : null;
  setFilterValue('chainIdEquals', chainId);
  setFilterValue('search', selectedProductGroup);
  setFilterValue('idExcludes', selectedProductGroupIds);

  return filter;
};

export const mapRawResponseItemToAutocompleteOption = (group: ProductGroupFragment) => ({
  value: group.id,
  label: group.name,
  description: group.description || '',
  _raw: group,
});

export const ProductGroupSelect: React.FC<IProductGroupSelectContainerProps> = ({
  makeFilter = defaultMakeFilter,
  value,
  ...props
}) => {
  const chainId = useChainId();

  const selectedProductGroupIds = React.useMemo(
    () => (value || []).map((productGroup) => productGroup.id),
    [value],
  );

  const makeFilterMemo = React.useCallback<
    NonNullable<
      ILazyAutocompleteProps<
        ProductGroupFragment,
        ReturnType<typeof useGetProductGroupsLazy>['response']
      >['makeQueryFilter']
    >
  >(
    (autocompleteInputValue) =>
      makeFilter(chainId, autocompleteInputValue, selectedProductGroupIds),
    [chainId, makeFilter, selectedProductGroupIds],
  );

  return (
    <LazyAutocomplete<ProductGroupFragment, ReturnType<typeof useGetProductGroupsLazy>['response']>
      label='Product Groups'
      value={value}
      {...props}
      useLazyQuery={useGetProductGroupsLazy}
      makeQueryFilter={makeFilterMemo}
      mapQueryResponseToRawArray={(response) => response.data || []}
      mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
      idExcludes={selectedProductGroupIds}
    />
  );
};

export const ProductGroupSelectField: React.FC<
  { name: string } & Omit<IProductGroupSelectContainerProps, 'value' | 'onChange'>
> = (props) => {
  const renderInput = useCallback<
    NonNullable<RenderableProps<FieldRenderProps<ProductGroupFragment[]>>['render']>
  >(
    ({ input, meta }) => (
      <ProductGroupSelect
        {...props}
        value={input.value}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        onChange={input.onChange}
        errorText={showErrorOnBlurOrDirty({ meta }) ? meta.error : undefined}
      />
    ),
    [props],
  );

  return <Field name={props.name} isEqual={_.isEqual} render={renderInput} />;
};
